package co.uk.paxes.pinpoint.web.ads

import kotlinx.browser.window
import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Manages undo credit balance for AI single-player games.
 * 
 * ⚠️ CRITICAL: Always reads FRESH from storage to avoid stale cache!
 * ⚠️ Auto-initializes to 2 credits if not present (matching RewardedAdManager)
 * 
 * Players can earn credits by watching rewarded video ads.
 * Credits are persisted in localStorage and capped at 10.
 */
@JsExport
object UndoCreditManager {
    
    private const val STORAGE_KEY = "pinpoint_undo_credits"
    private const val INITIAL_CREDITS = 2  // Start with 2 credits (matching RewardedAdManager)
    private const val MAX_CREDITS = 10  // AI games allow up to 10 credits
    private const val CREDITS_PER_AD = 1
    
    private var initialized = false
    
    /**
     * Initialize: ensures credits are set to at least INITIAL_CREDITS if not present.
     */
    @JsName("initialize")
    fun initialize() {
        if (initialized) return
        
        val stored = co.uk.paxes.pinpoint.web.PlayGamaStorageManager.getItem(STORAGE_KEY)
        if (stored == null) {
            co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(STORAGE_KEY, INITIAL_CREDITS.toString())
            console.log("📂 UndoCreditManager.initialize(): No credits found, initializing to $INITIAL_CREDITS")
        } else {
            val currentValue = stored.toIntOrNull() ?: 0
            if (currentValue == 0) {
                co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(STORAGE_KEY, INITIAL_CREDITS.toString())
                console.log("📂 UndoCreditManager.initialize(): Found 0 credits, resetting to $INITIAL_CREDITS")
            }
        }
        initialized = true
    }
    
    /**
     * Get current credit balance.
     * ⚠️ ALWAYS reads fresh from storage to avoid stale cache!
     */
    @JsName("getCredits")
    fun getCredits(): Int {
        // Ensure initialized on first read
        if (!initialized) {
            initialize()
        }
        
        // Always re-read from storage to ensure we have the latest value
        val stored = co.uk.paxes.pinpoint.web.PlayGamaStorageManager.getItem(STORAGE_KEY)
        
        val currentCredits = if (stored != null) {
            try {
                val parsed = stored.toInt()
                val clamped = minOf(maxOf(parsed, 0), MAX_CREDITS)
                console.log("📊 UndoCreditManager.getCredits(): stored=$stored, parsed=$parsed, clamped=$clamped, MAX_CREDITS=$MAX_CREDITS")
                clamped
            } catch (e: Exception) {
                console.error("❌ UndoCreditManager: Failed to parse credits: $stored -> ${e.message}")
                0
            }
        } else {
            console.warn("⚠️ UndoCreditManager.getCredits(): No value in storage (key=$STORAGE_KEY)")
            0
        }
        
        return currentCredits
    }
    
    /**
     * Add credits from watching an ad.
     * Enforces maximum of 10 credits.
     * 
     * @param amount Number of credits to add (default 1)
     */
    @JsName("addCredits")
    fun addCredits(amount: Int = CREDITS_PER_AD) {
        val oldCredits = getCredits()
        val newCredits = minOf(oldCredits + amount, MAX_CREDITS)
        
        if (newCredits != oldCredits) {
            co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(STORAGE_KEY, newCredits.toString())
            console.log("💰 UndoCreditManager: Added $amount credits ($oldCredits → $newCredits)")
        } else {
            console.log("⚠️ UndoCreditManager: Already at maximum credits ($MAX_CREDITS)")
        }
    }
    
    /**
     * Consume a credit for undo.
     * Returns true if credit was available and consumed.
     */
    @JsName("consumeCredit")
    fun consumeCredit(): Boolean {
        val currentCredits = getCredits()
        
        if (currentCredits > 0) {
            val newCredits = currentCredits - 1
            co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(STORAGE_KEY, newCredits.toString())
            console.log("💸 UndoCreditManager: Consumed 1 credit (remaining: $newCredits)")
            return true
        }
        
        console.log("❌ UndoCreditManager: No credits available")
        return false
    }
    
    /**
     * Check if at maximum credits.
     */
    @JsName("isAtMaxCredits")
    fun isAtMaxCredits(): Boolean {
        return getCredits() >= MAX_CREDITS
    }
    
    /**
     * Load credits from storage (no-op, since getCredits always reads fresh).
     */
    @JsName("loadCredits")
    fun loadCredits() {
        initialize()
        console.log("📂 UndoCreditManager: Credits available (reading fresh from storage): ${getCredits()}")
    }
    
    /**
     * Reset credits to zero (for testing).
     */
    @JsName("resetCredits")
    fun resetCredits() {
        co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(STORAGE_KEY, "0")
        console.log("🔄 UndoCreditManager: Reset credits to 0")
    }
    
    /**
     * Get maximum credits allowed.
     */
    @JsName("getMaxCredits")
    fun getMaxCredits(): Int = MAX_CREDITS
}
